<template>
  <BaseDialog
    title="預約者備註欄位設定"
    :close-on-click-modal="false"
    width="610px"
    :btn1-loading="loading"
    @close="$emit('close')"
    @cancel="$emit('close')"
    @confirm="onConfirm"
  >
    <div style="margin-bottom: 40px">
      <BobForm
        :formRef.sync="formRef"
        :model="formData"
        :fields="displayFields"
        :rules="formRules"
        label-position="top"
      />
    </div>
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, onBeforeMount, provide } from 'vue'
import { get } from 'lodash'
import { UpdateReservationParameter } from '@/api/reservation'
import BobForm from '@/components/BobForm.vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import FormItemDescription from '@/components/FormItem/FormItemDescription.vue'
import FormItemTooltip from '@/components/FormItem/FormItemTooltip.vue'
import notifyMessage from '@/config/notifyMessage'
import { useShop } from '@/use/shop'
import { useBaseForm } from '@/use/useForm'
import { maxRules, noEmptyRules } from '@/validation'

const DEFAULT_USER_COMMENT_PLACEHOLDER = '請輸入備註'

export default defineComponent({
  name: 'EditAppointmentUserCommentSettingsModal',
  components: {
    BobForm,
    BaseDialog,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formRef, formData, initFormData, checkForm, loading } = useBaseForm()

    initFormData({
      userCommentHint: '',
      userCommentPlaceholder: DEFAULT_USER_COMMENT_PLACEHOLDER,
      userCommentRequired: false,
    })

    const displayFields = computed(() => {
      return [
        {
          prop: 'userCommentHint',
          label: '備註提示文字',
          component: FormItemDescription,
          props: {},
          body: {
            component: 'BaseElInput',
            props: {
              placeholder: '請輸入文字',
              maxlength: 20,
              showWordLimit: true,
            },
          },
        },
        {
          prop: 'userCommentPlaceholder',
          label: '範例文字',
          component: FormItemTooltip,
          props: {
            required: true,
            tooltip: '會員輸入文字前可看到的參考文字範例。',
            tooltipWidth: 200,
            iconSize: 20,
          },
          body: {
            component: 'BaseElInput',
            props: {
              placeholder: DEFAULT_USER_COMMENT_PLACEHOLDER,
            },
          },
        },
        {
          prop: 'userCommentRequired',
          label: '欄位必填',
          body: {
            component: 'BaseElSwitch',
            props: {
              activeText: '開啟',
              inactiveText: '關閉',
            },
          },
        },
      ]
    })

    const formRules = {
      userCommentHint: [maxRules(20)],
      userCommentPlaceholder: [noEmptyRules('請輸入資料')],
    }

    const submitPayload = computed(() => {
      return {
        shopId: shopId.value,
        userCommentSetting: {
          userCommentHint: formData.userCommentHint,
          userCommentPlaceholder: formData.userCommentPlaceholder,
          userCommentRequired: formData.userCommentRequired,
        },
      }
    })

    const syncFormData = () => {
      formData.userCommentHint = get(props.configData, 'userCommentSetting.userCommentHint', '')
      formData.userCommentPlaceholder = get(
        props.configData,
        'userCommentSetting.userCommentPlaceholder',
        DEFAULT_USER_COMMENT_PLACEHOLDER,
      )
      formData.userCommentRequired = get(props.configData, 'userCommentSetting.userCommentRequired', false)
    }

    const onConfirm = async () => {
      if (loading.value) return
      const isValid = await checkForm(formRef.value)
      if (!isValid) {
        loading.value = false
        return
      }

      loading.value = true
      const [, errMsg] = await UpdateReservationParameter(submitPayload.value)
      loading.value = false

      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      window.$message.success(notifyMessage.updateSuccess)
      emit('refresh')
      emit('close')
    }

    provide('formData', formData)

    onBeforeMount(() => {
      syncFormData()
    })

    return {
      formRef,
      formData,
      displayFields,
      formRules,
      loading,
      onConfirm,
    }
  },
})
</script>
